import type {
  KambiCouponPlaceErrorResponse,
  KambiCouponValidateFailResponse,
  KambiCouponValidationError,
} from '@mobi/api-types/kambi/player/coupon'
import type { KambiBettingParams } from '@core/Data/Betting/kambiSports/types'
import {
  ConfirmKambiBetFailed,
  KambiBetResponsePriceChange,
  ProposeKambiBetFailed,
} from '@core/Areas/Quickbet/signals'

export function handleResponseErrors({
  requestType,
  request,
  response,
  responseHttpCode,
}: {
  requestType: 'propose' | 'commit'
  request: Request
  response: Response
  responseHttpCode: number
}) {
  if (responseHttpCode === 401) {
    requestType === 'propose'
      ? ProposeKambiBetFailed('Mobi Not Logged In')
      : ConfirmKambiBetFailed('Mobi Not Logged In')
    return
  }

  if (responseHttpCode === 409) {
    const priceChangeErrors = getPriceChangeErrors(response)
    if (priceChangeErrors.length > 0) {
      const matchedOutcomeIdsWithNewPrice = mapPriceChangeToOutcomeIds(priceChangeErrors, request)
      KambiBetResponsePriceChange(matchedOutcomeIdsWithNewPrice)
      return
    }

    const rewardStakeError = requestType === 'commit' && getRewardStakeError(response)
    if (rewardStakeError) {
      const error: RewardStakeErrorParam =
        rewardStakeError.type === 'STAKE_TOO_LOW_FOR_REWARD'
          ? 'Reward Stake Too Low'
          : 'Reward Stake Too High'
      ConfirmKambiBetFailed(error)
      return
    }

    const rewardsNotAvailable = getRewardsNotAvailable(response)
    if (rewardsNotAvailable.length > 0) {
      requestType === 'propose'
        ? ProposeKambiBetFailed('Reward Not Available')
        : ConfirmKambiBetFailed('Reward Not Available')
      return
    }

    if (hasBettingClosedErrors(response)) {
      requestType === 'propose'
        ? ProposeKambiBetFailed('Betting Closed')
        : ConfirmKambiBetFailed('Betting Closed')
      return
    }

    if (
      requestType === 'commit' &&
      response.generalErrors.some(error => error.type === 'NOT_ENOUGH_FUNDS')
    ) {
      ConfirmKambiBetFailed('Insufficient Funds')
      return
    }
  }

  if (hasStakeTooHighError(response)) {
    const limit = getStakeTooHighLimit(response) || 0
    requestType === 'propose'
      ? ProposeKambiBetFailed({
          type: 'Stake Too High',
          limit,
        })
      : ConfirmKambiBetFailed({
          type: 'Stake Too High',
          limit,
        })
    return
  }

  if (hasPayoutTooHighError(response)) {
    requestType === 'propose'
      ? ProposeKambiBetFailed('Payout Too High')
      : ConfirmKambiBetFailed('Payout Too High')
    return
  }

  requestType === 'propose' ? ProposeKambiBetFailed() : ConfirmKambiBetFailed()
}

// =============
// Local Helpers
// =============

function getPriceChangeErrors(response: Response): PriceChangeErrors {
  return response.couponRowErrors.flatMap(({ errors, couponRowIndex }) =>
    errors.flatMap(error => (error.type === 'ODDS_CHANGED' ? { error, couponRowIndex } : []))
  )
}

const rewardStakeErrorTypes: ErrorType[] = [
  'STAKE_TOO_LOW_FOR_REWARD',
  'PBT_STAKE_TOO_HIGH',
  'PBT_WINNINGS_TOO_HIGH',
]
function getRewardStakeError(response: Response): KambiCouponValidationError | false {
  const rewardStakeErrors = response.generalErrors.flatMap(error =>
    rewardStakeErrorTypes.includes(error.type) ? error : []
  )
  return rewardStakeErrors.length > 0 ? rewardStakeErrors[0] : false
}

const rewardNotAvailableErrorTypes: ErrorType[] = [
  'TOO_MANY_OUTCOMES_FOR_REWARD',
  'TOO_FEW_OUTCOMES_FOR_REWARD',
  'REWARD_NOT_AVAILABLE',
  'COMBINATION_ODDS_ABOVE_REWARD_REQUIREMENT',
  'ODDS_BELOW_REWARD_REQUIREMENT',
]
function getRewardsNotAvailable(response: Response): KambiCouponValidationError[] {
  return response.generalErrors.flatMap(error =>
    rewardNotAvailableErrorTypes.includes(error.type) ? error : []
  )
}

function mapPriceChangeToOutcomeIds(priceChangeErrors: PriceChangeErrors, request: Request) {
  return priceChangeErrors.flatMap(priceChange => {
    const couponRow = request.couponRows.find(
      couponRow => couponRow.index === priceChange.couponRowIndex
    )
    if (!couponRow) return []
    return {
      outcomeIds: couponRow.group
        ? couponRow.group.groups.flatMap(group => group.outcomeIds)
        : [couponRow.outcomeId],
      odds: Number(priceChange.error.arguments[0].value) || null,
    }
  })
}

const bettingClosedErrorTypes: ErrorType[] = [
  'BET_OFFER_CLOSED',
  'BET_OFFER_SUSPENDED',
  'MIXED_LIVE_AND_PRE_MATCH',
]
function hasBettingClosedErrors(response: Response) {
  const { generalErrors, betErrors, couponRowErrors } = response
  return (
    generalErrors.some(({ type }) => bettingClosedErrorTypes.includes(type)) ||
    betErrors.some(({ errors }) =>
      errors.some(({ type }) => bettingClosedErrorTypes.includes(type))
    ) ||
    couponRowErrors.some(({ errors }) =>
      errors.some(({ type }) => bettingClosedErrorTypes.includes(type))
    )
  )
}

function hasStakeTooHighError(response: Response) {
  return (
    response.generalErrors?.some(e => e.type === 'STAKE_TOO_HIGH') ||
    response.betErrors?.some(b => b.errors?.some(e => e.type === 'STAKE_TOO_HIGH')) ||
    response.couponRowErrors?.some(c => c.errors?.some(e => e.type === 'STAKE_TOO_HIGH'))
  )
}

function hasPayoutTooHighError(response: Response) {
  return (
    response.generalErrors?.some(({ type }) => type === 'PAYOUT_TOO_HIGH') ||
    response.betErrors?.some(({ errors }) =>
      errors.some(({ type }) => type === 'PAYOUT_TOO_HIGH')
    ) ||
    response.couponRowErrors?.some(({ errors }) =>
      errors.some(({ type }) => type === 'PAYOUT_TOO_HIGH')
    )
  )
}

function getStakeTooHighLimit(response: Response): number | null {
  const allErrors = [
    ...response.generalErrors,
    ...response.betErrors.flatMap(b => b.errors),
    ...response.couponRowErrors.flatMap(c => c.errors),
  ]

  const error = allErrors.find(e => e.type === 'STAKE_TOO_HIGH')
  if (!error) return null

  return Number(error.arguments?.[0]?.value ?? null)
}

// =====
// Types
// =====

type Request = Pick<KambiBettingParams, 'bets' | 'couponRows'>
type Response = KambiCouponValidateFailResponse | KambiCouponPlaceErrorResponse

type PriceChangeErrors = { error: KambiCouponValidationError; couponRowIndex: number }[]
type ErrorType = KambiCouponValidationError['type']

type RewardStakeErrorParam = Extract<
  NonNullable<Parameters<typeof ConfirmKambiBetFailed>[0]>,
  'Reward Stake Too High' | 'Reward Stake Too Low'
>
